import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, Outlet } from "react-router-dom";
import { PortalGuard, usePortalAuth, portalApi } from "./PortalAuth";
import { LogOut, Package, Home, PlusCircle, Sparkles, ArrowRight, CheckCircle2, AlertCircle, Truck, Upload, FileDown, Layers, DollarSign, BookOpen, Trash2, Star, RotateCcw, X, Users, Send, Copy, ShieldCheck, Bell, Warehouse } from "lucide-react";

export const PortalLogin = () => {
  const { login } = usePortalAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(""); const [pw, setPw] = useState(""); const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true); setErr("");
    try { await login(email, pw); nav("/portal", { replace: true }); }
    catch (e) { setErr(e?.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <PortalShell>
      <form onSubmit={submit} className="card p-8 max-w-md mx-auto space-y-4">
        <div><h1 className="font-display text-3xl font-bold text-[var(--tt-navy)]">Sign in</h1><p className="text-sm text-[var(--tt-muted)] mt-1">Access your Turbo Transit customer portal.</p></div>
        <button type="button" onClick={googleSignIn} data-testid="portal-google-signin" className="w-full flex items-center justify-center gap-3 rounded-xl border border-[var(--tt-line)] bg-white hover:bg-[var(--tt-mint-soft)] transition py-3 text-sm font-semibold text-[var(--tt-navy)]">
          <svg viewBox="0 0 24 24" className="h-5 w-5"><path fill="#EA4335" d="M12 5c1.6 0 3 .5 4 1.5l3-3C17.2 1.7 14.7.6 12 .6 7.5.6 3.7 3.2 1.9 7l3.5 2.7C6.3 6.9 8.9 5 12 5z"/><path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.2-2.3H12v4.4h6.5c-.1 1.1-.9 2.7-2.5 3.9l3.5 2.7c2.1-1.9 3.3-4.7 3.3-8.1z"/><path fill="#FBBC05" d="M5.4 14.3C5.1 13.5 5 12.7 5 12s.1-1.5.4-2.3L1.9 7c-.9 1.5-1.4 3.3-1.4 5s.5 3.5 1.4 5l3.5-2.7z"/><path fill="#34A853" d="M12 23.5c3.4 0 6.2-1.1 8.3-3.1l-3.5-2.7c-1 .7-2.4 1.2-4.8 1.2-3.1 0-5.7-1.9-6.6-4.7L1.9 17C3.7 20.8 7.5 23.5 12 23.5z"/></svg>
          Sign in with Google
        </button>
        <div className="relative"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[var(--tt-line)]" /></div><div className="relative flex justify-center text-xs uppercase tracking-widest"><span className="bg-white px-3 text-[var(--tt-muted)]">or</span></div></div>
        <Input label="Email" value={email} onChange={setEmail} type="email" testid="portal-login-email" />
        <Input label="Password" value={pw} onChange={setPw} type="password" testid="portal-login-password" />
        {err && <div className="rounded-lg bg-red-50 text-red-600 text-xs p-3 flex gap-2"><AlertCircle className="h-4 w-4 mt-0.5"/>{err}</div>}
        <button disabled={busy} data-testid="portal-login-submit" className="btn-primary w-full justify-center">{busy ? "Signing in…" : "Sign in with email"}</button>
        <p className="text-center text-sm text-[var(--tt-muted)]">New here? <Link to="/portal/register" className="text-[var(--tt-green)] font-semibold hover:underline">Create an account</Link></p>
      </form>
    </PortalShell>
  );
};

export const PortalRegister = () => {
  const { register } = usePortalAuth();
  const nav = useNavigate();
  const inviteToken = new URLSearchParams(window.location.search).get("invite") || "";
  const [invitePreview, setInvitePreview] = useState(null);
  const [type, setType] = useState("individual");
  const [form, setForm] = useState({ email: "", password: "", full_name: "", company: "", phone: "", city: "", state: "", postal_code: "", country: "United States", tax_id: "", industry: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!inviteToken) return;
    portalApi.lookupInvite(inviteToken).then((r) => {
      setInvitePreview(r.data);
      setForm((p) => ({ ...p, email: r.data.email }));
    }).catch(() => {});
  }, [inviteToken]);

  const submit = async (e) => { e.preventDefault(); setBusy(true); setErr("");
    try { await register({ ...form, account_type: type, invite_token: inviteToken || undefined }); nav("/portal", { replace: true }); }
    catch (e) { setErr(e?.response?.data?.detail || "Registration failed"); }
    finally { setBusy(false); }
  };
  return (
    <PortalShell>
      <form onSubmit={submit} className="card p-8 max-w-lg mx-auto space-y-4">
        <div><h1 className="font-display text-3xl font-bold text-[var(--tt-navy)]">Create your account</h1><p className="text-sm text-[var(--tt-muted)] mt-1">Ship, track, and manage your parcels globally.</p></div>
        {invitePreview && (
          <div className="rounded-lg bg-[var(--tt-mint-soft)] text-[var(--tt-navy)] text-sm p-3" data-testid="invite-banner">
            You're joining <b>{invitePreview.organization.name}</b> — invited by {invitePreview.invited_by}.
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          {["individual", "business"].map((t) => (
            <button type="button" key={t} data-testid={`portal-type-${t}`} onClick={() => setType(t)} className={`rounded-xl border p-3 text-left transition ${type === t ? "border-[var(--tt-green)] bg-[var(--tt-mint-soft)]" : "border-[var(--tt-line)] hover:border-[var(--tt-green)]/50"}`}>
              <div className="font-semibold capitalize text-[var(--tt-navy)]">{t}</div>
              <div className="text-xs text-[var(--tt-muted)] mt-0.5">{t === "individual" ? "Personal shipments" : "For companies with EIN"}</div>
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input label="Email" value={form.email} onChange={(v) => set("email", v)} type="email" testid="portal-reg-email" />
          <Input label="Password" value={form.password} onChange={(v) => set("password", v)} type="password" testid="portal-reg-password" />
          {type === "individual" ? (
            <Input label="Full name" value={form.full_name} onChange={(v) => set("full_name", v)} testid="portal-reg-name" />
          ) : (
            <>
              <Input label="Company name *" value={form.company} onChange={(v) => set("company", v)} testid="portal-reg-company" />
              <Input label="Owner / primary contact" value={form.full_name} onChange={(v) => set("full_name", v)} />
              <Input label="Tax ID / EIN" value={form.tax_id} onChange={(v) => set("tax_id", v)} />
              <Input label="Industry" value={form.industry} onChange={(v) => set("industry", v)} />
            </>
          )}
          <Input label="Phone" value={form.phone} onChange={(v) => set("phone", v)} />
          <Input label="City" value={form.city} onChange={(v) => set("city", v)} />
          <Input label="State" value={form.state} onChange={(v) => set("state", v)} />
          <Input label="ZIP / Postal" value={form.postal_code} onChange={(v) => set("postal_code", v)} />
        </div>
        {err && <div className="rounded-lg bg-red-50 text-red-600 text-xs p-3 flex gap-2"><AlertCircle className="h-4 w-4 mt-0.5"/>{err}</div>}
        <button disabled={busy} data-testid="portal-reg-submit" className="btn-primary w-full justify-center">{busy ? "Creating…" : "Create account"}</button>
        <p className="text-center text-sm text-[var(--tt-muted)]">Already have an account? <Link to="/portal/login" className="text-[var(--tt-green)] font-semibold hover:underline">Sign in</Link></p>
      </form>
    </PortalShell>
  );
};

const PortalShell = ({ children, headerRight }) => (
  <div className="min-h-screen bg-[var(--tt-bg)]">
    <header className="border-b border-[var(--tt-line)] bg-white">
      <div className="mx-auto max-w-5xl px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2"><Truck className="h-5 w-5 text-[var(--tt-green)]" /><span className="font-display font-bold text-[var(--tt-navy)]">Turbo Transit</span><span className="text-xs text-[var(--tt-muted)]">· Customer Portal</span></Link>
        <div className="flex items-center gap-4">
          {headerRight}
          <Link to="/" className="text-sm text-[var(--tt-muted)] hover:text-[var(--tt-navy)]">← Back to site</Link>
        </div>
      </div>
    </header>
    <main className="mx-auto max-w-5xl px-4 py-10">{children}</main>
  </div>
);

// ─── Notification Bell (polling every 30s + toast on new) ────────────────────
const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const seenRef = useRef(new Set());
  const nav = useNavigate();

  const fetchNotifs = async (isFirst = false) => {
    try {
      const r = await portalApi.notifications({ limit: 15 });
      setItems(r.data.notifications || []);
      setUnread(r.data.unread_count || 0);
      // Toast for any items we haven't seen before (skip first tick)
      if (!isFirst) {
        const fresh = (r.data.notifications || []).find((n) => !n.read && !seenRef.current.has(n.id));
        if (fresh) setToast(fresh);
      }
      (r.data.notifications || []).forEach((n) => seenRef.current.add(n.id));
    } catch {}
  };

  useEffect(() => {
    fetchNotifs(true);
    const t = setInterval(() => { if (!document.hidden) fetchNotifs(false); }, 30000);
    const onVisible = () => { if (!document.hidden) fetchNotifs(false); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  const markAllRead = async () => {
    await portalApi.markNotificationsRead(null);
    setUnread(0);
    setItems((p) => p.map((n) => ({ ...n, read: true })));
  };

  const openNotif = async (n) => {
    if (!n.read) {
      await portalApi.markNotificationsRead([n.id]);
      setUnread((u) => Math.max(0, u - 1));
      setItems((p) => p.map((x) => x.id === n.id ? { ...x, read: true } : x));
    }
    setOpen(false);
    if (n.shipment_id) nav("/portal");
  };

  return (
    <>
      <div className="relative">
        <button onClick={() => setOpen(!open)} data-testid="notif-bell" className="relative rounded-full hover:bg-[var(--tt-mint-soft)] p-2 transition">
          <Bell className="h-5 w-5 text-[var(--tt-navy)]" />
          {unread > 0 && (
            <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 bg-[var(--tt-zam-orange)] text-white text-[10px] font-bold rounded-full h-4 min-w-[16px] px-1 grid place-items-center">{unread > 9 ? "9+" : unread}</span>
          )}
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <div className="absolute right-0 top-full mt-2 z-40 w-96 max-h-[70vh] bg-white rounded-2xl border border-[var(--tt-line)] shadow-xl overflow-hidden" data-testid="notif-panel">
              <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--tt-line)]">
                <div className="font-semibold text-[var(--tt-navy)]">Notifications</div>
                {unread > 0 && <button onClick={markAllRead} data-testid="notif-mark-all-read" className="text-xs text-[var(--tt-green)] hover:underline">Mark all read</button>}
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                {items.length === 0 && <div className="p-8 text-center text-sm text-[var(--tt-muted)]">You're all caught up.</div>}
                {items.map((n) => {
                  const icon = n.kind?.includes("delivered") ? <CheckCircle2 className="h-4 w-4 text-[var(--tt-green)]" />
                    : n.kind?.includes("out_for_delivery") ? <Truck className="h-4 w-4 text-[var(--tt-zam-orange)]" />
                    : <Package className="h-4 w-4 text-[var(--tt-navy)]" />;
                  return (
                    <button key={n.id} onClick={() => openNotif(n)} data-testid={`notif-item-${n.id}`} className={`w-full text-left px-4 py-3 border-b border-[var(--tt-line)] flex items-start gap-3 hover:bg-[var(--tt-mint-soft)] transition ${!n.read ? "bg-[var(--tt-mint-soft)]/40" : ""}`}>
                      <div className="mt-0.5">{icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm ${!n.read ? "font-semibold text-[var(--tt-navy)]" : "text-[var(--tt-ink-soft)]"}`}>{n.message}</div>
                        {n.meta?.tracking_code && <div className="text-xs font-mono text-[var(--tt-muted)] mt-0.5">{n.meta.tracking_code}</div>}
                        <div className="text-xs text-[var(--tt-muted)] mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                      </div>
                      {!n.read && <div className="h-2 w-2 rounded-full bg-[var(--tt-zam-orange)] mt-2" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm animate-in slide-in-from-bottom-6" data-testid="notif-toast">
          <div className="bg-white rounded-xl shadow-2xl border-l-4 border-[var(--tt-green)] p-4 flex gap-3">
            <CheckCircle2 className="h-5 w-5 text-[var(--tt-green)] mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-[var(--tt-navy)] text-sm">{toast.message}</div>
              {toast.meta?.tracking_code && <div className="text-xs font-mono text-[var(--tt-muted)] mt-0.5">{toast.meta.tracking_code}</div>}
            </div>
            <button onClick={() => setToast(null)} className="text-[var(--tt-muted)] hover:text-[var(--tt-navy)]"><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </>
  );
};

export const PortalLayout = () => {
  const { customer, logout } = usePortalAuth();
  const nav = useNavigate();
  return (
    <PortalShell headerRight={<NotificationBell />}>
      <div className="grid md:grid-cols-[220px_1fr] gap-6">
        <aside className="space-y-1">
          {[{ to: "/portal", label: "Dashboard", icon: Home, end: true },
            { to: "/portal/new", label: "New shipment", icon: PlusCircle },
            { to: "/portal/fulfillment", label: "Fulfillment", icon: Warehouse },
            { to: "/portal/addresses", label: "Address book", icon: BookOpen },
            { to: "/portal/templates", label: "Delivery emails", icon: Send },
            ...(customer?.account_type === "business" || customer?.organization_id ? [
              { to: "/portal/bulk", label: "Bulk shipping", icon: Layers },
              { to: "/portal/team", label: "Team", icon: Users },
            ] : []),
            { to: "/portal/subscribe", label: "Subscription", icon: Sparkles }].map((n) => {
            const Icon = n.icon;
            return (
              <NavItem key={n.to} to={n.to} end={n.end}><Icon className="h-4 w-4" /> {n.label}</NavItem>
            );
          })}
          <div className="pt-4 mt-4 border-t border-[var(--tt-line)] text-xs text-[var(--tt-muted)] px-3">
            <div className="truncate">{customer?.email}</div>
            <div className="uppercase tracking-widest text-[10px] mt-0.5">{customer?.account_type} · {customer?.subscription_tier !== "none" ? "Subscribed" : "Free"}</div>
          </div>
          <button data-testid="portal-logout" onClick={async () => { await logout(); nav("/portal/login"); }} className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--tt-muted)] hover:text-red-600 transition"><LogOut className="h-4 w-4" /> Sign out</button>
        </aside>
        <div><Outlet /></div>
      </div>
    </PortalShell>
  );
};

const NavItem = ({ to, end, children }) => (
  <Link to={to} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-[var(--tt-ink-soft)] hover:bg-white hover:text-[var(--tt-navy)] transition">{children}</Link>
);

export const PortalDashboard = () => {
  const { customer } = usePortalAuth();
  const [ships, setShips] = useState([]);
  const [returning, setReturning] = useState(null);
  const refresh = () => portalApi.listShipments().then((r) => setShips(r.data.shipments || []));
  useEffect(() => { refresh(); }, []);
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-[var(--tt-navy)]">Welcome{customer?.full_name ? `, ${customer.full_name.split(" ")[0]}` : ""}</h1>
        <p className="text-[var(--tt-muted)] mt-1">Your shipments in one place.</p>
      </div>
      {customer?.subscription_tier === "none" && (
        <div className="rounded-2xl bg-[var(--tt-mint-soft)] border border-[var(--tt-mint)] p-5 flex items-center justify-between gap-4">
          <div><div className="font-semibold text-[var(--tt-navy)]">Save on every shipment</div><div className="text-sm text-[var(--tt-ink-soft)] mt-1">Subscribe to save {customer?.account_type === "business" ? "10%" : "5%"} on every label.</div></div>
          <Link to="/portal/subscribe" className="btn-primary text-sm !py-2.5">See plans <ArrowRight className="h-4 w-4" /></Link>
        </div>
      )}
      <Link to="/portal/new" data-testid="portal-new-shipment-cta" className="block card p-6 hover:!border-[var(--tt-green)]">
        <div className="flex items-center gap-3"><div className="h-10 w-10 rounded-xl bg-[var(--tt-green)] text-white grid place-items-center"><PlusCircle className="h-5 w-5" /></div>
        <div><div className="font-semibold text-[var(--tt-navy)]">New shipment</div><div className="text-xs text-[var(--tt-muted)]">Live rates · buy label · schedule pickup.</div></div></div>
      </Link>
      <div>
        <div className="text-xs uppercase tracking-widest text-[var(--tt-muted)] font-semibold mb-3">Recent shipments</div>
        {ships.length === 0 ? (
          <div className="card p-10 text-center"><Package className="h-8 w-8 mx-auto text-[var(--tt-muted)]" /><p className="mt-3 text-sm text-[var(--tt-muted)]">No shipments yet.</p></div>
        ) : (
          <div className="space-y-2">
            {ships.map((s) => (
              <div key={s.id} className="card p-4 flex items-center justify-between" data-testid={`shipment-row-${s.id}`}>
                <div>
                  <div className="font-mono text-xs text-[var(--tt-muted)]">{s.tracking_code || s.tracking_number || s.ep_shipment_id}</div>
                  <div className="text-sm font-semibold text-[var(--tt-navy)]">{s.to_address?.name || s.to_name || "—"} · {s.to_address?.city || s.to_city || "—"}, {s.to_address?.country || s.to_country || ""}</div>
                  <div className="text-xs text-[var(--tt-muted)]">{s.carrier ? `${s.carrier} · ${(s.service || "").replace(/_/g, " ")}` : `Status: ${s.status}`}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg font-bold text-[var(--tt-navy)]">${(s.total ?? 0).toFixed(2)}</div>
                  <div className="inline-flex items-center gap-1 rounded-full bg-[var(--tt-mint-soft)] text-[var(--tt-green)] px-2 py-0.5 text-[11px] font-semibold mt-1"><CheckCircle2 className="h-3 w-3" />{(s.status || "").replace(/_/g, " ")}</div>
                  {s.label_url && s.ep_shipment_id && <div className="mt-1 flex items-center justify-end gap-3">
                    <a href={portalApi.labelUrl(s.ep_shipment_id)} target="_blank" rel="noreferrer" className="text-[11px] text-[var(--tt-green)] hover:underline" data-testid={`download-label-${s.id}`}>Download label</a>
                    {s.return_label_url ? (
                      <a href={portalApi.returnLabelUrl(s.ep_shipment_id)} target="_blank" rel="noreferrer" className="text-[11px] text-[var(--tt-zam-orange)] hover:underline" data-testid={`return-dl-${s.id}`}><RotateCcw className="h-3 w-3 inline mr-0.5" />Return label</a>
                    ) : (
                      <button onClick={() => setReturning(s)} data-testid={`return-btn-${s.id}`} className="text-[11px] text-[var(--tt-zam-orange)] hover:underline"><RotateCcw className="h-3 w-3 inline mr-0.5" />Request return</button>
                    )}
                  </div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {returning && <ReturnLabelModal shipment={returning} onClose={() => setReturning(null)} onDone={refresh} />}
    </div>
  );
};

export const PortalSubscribe = () => {
  const { customer, setCustomer } = usePortalAuth();
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => { portalApi.pricing().then((r) => setPricing(r.data)); }, []);
  const pick = async (tier) => {
    setBusy(tier); setErr("");
    try {
      if (tier === "none") {
        const r = await portalApi.subscribe("none", window.location.origin);
        setCustomer({ ...customer, subscription_tier: r.data.tier, subscription_status: "canceled" });
        return;
      }
      const r = await portalApi.subscribe(tier, window.location.origin);
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        setErr("Could not start checkout — no URL returned.");
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start checkout.");
    } finally { setBusy(""); }
  };
  if (!pricing) return null;
  const tiers = [{ key: "individual", ...pricing.tiers.individual }, { key: "business", ...pricing.tiers.business }];
  const canceled = new URLSearchParams(window.location.search).get("canceled");
  return (
    <div className="space-y-6">
      <div><h1 className="font-display text-3xl font-bold text-[var(--tt-navy)]">Subscription</h1><p className="text-[var(--tt-muted)] mt-1">Save on every label. Cancel anytime.</p></div>
      {canceled && <div className="rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm p-3">Checkout canceled. No charge was made.</div>}
      {err && <div className="rounded-lg bg-red-50 text-red-700 text-sm p-3 flex gap-2"><AlertCircle className="h-4 w-4 mt-0.5"/>{err}</div>}
      <div className="grid md:grid-cols-2 gap-4">
        {tiers.map((t) => {
          const eligible = t.available_for.includes(customer.account_type);
          const active = customer.subscription_tier === t.key;
          return (
            <div key={t.key} className={`card p-6 ${active ? "!border-[var(--tt-green)] bg-[var(--tt-mint-soft)]" : ""}`}>
              <div className="flex items-baseline gap-1"><span className="font-display text-4xl font-bold text-[var(--tt-navy)]">${t.price}</span><span className="text-sm text-[var(--tt-muted)]">/ month</span></div>
              <div className="mt-1 text-xs uppercase tracking-widest text-[var(--tt-zam-orange)] font-semibold">{t.key} plan</div>
              <ul className="mt-4 space-y-2 text-sm text-[var(--tt-ink-soft)]">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[var(--tt-green)] mt-0.5"/> <b>{t.discount_pct}% off</b> every shipment</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[var(--tt-green)] mt-0.5"/> Priority pickup slots</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[var(--tt-green)] mt-0.5"/> Cancel anytime</li>
              </ul>
              <button disabled={!eligible || busy === t.key || active} data-testid={`subscribe-${t.key}`} onClick={() => pick(t.key)} className="btn-primary w-full justify-center mt-5 disabled:opacity-50">
                {active ? "Current plan" : busy === t.key ? "Redirecting…" : eligible ? `Subscribe · $${t.price}/mo` : `Not available for ${customer.account_type}`}
              </button>
              <div className="mt-3 text-[11px] text-[var(--tt-muted)] text-center">Secure checkout by Stripe · card, Apple Pay, Google Pay</div>
            </div>
          );
        })}
      </div>
      {customer.subscription_tier !== "none" && (
        <button data-testid="subscribe-cancel" onClick={() => pick("none")} disabled={busy === "none"} className="text-sm text-red-600 hover:underline">{busy === "none" ? "Canceling…" : "Cancel subscription"}</button>
      )}
    </div>
  );
};

export const PortalSubscribeSuccess = () => {
  const nav = useNavigate();
  const { customer, setCustomer } = usePortalAuth();
  const [state, setState] = useState({ status: "polling", tries: 0 });
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", msg: "Missing session id" }); return; }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const r = await portalApi.subscribeStatus(sessionId);
        if (r.data.payment_status === "paid") {
          // Refresh customer
          const me = await portalApi.me();
          setCustomer(me.data.customer);
          setState({ status: "paid", tier: r.data.tier });
          return;
        }
        if (r.data.status === "expired" || r.data.payment_status === "expired") {
          setState({ status: "expired" });
          return;
        }
        if (tries > 20) { setState({ status: "timeout" }); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: "error", msg: e?.response?.data?.detail || "Could not confirm payment." });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, setCustomer]);

  return (
    <div className="card p-10 text-center space-y-4 max-w-md mx-auto" data-testid="subscribe-success">
      {state.status === "polling" && <>
        <div className="mx-auto h-12 w-12 rounded-full border-4 border-[var(--tt-green)] border-t-transparent animate-spin" />
        <h2 className="font-display text-2xl font-bold text-[var(--tt-navy)]">Confirming payment…</h2>
        <p className="text-sm text-[var(--tt-muted)]">Hold tight — this takes a couple of seconds.</p>
      </>}
      {state.status === "paid" && <>
        <div className="mx-auto h-14 w-14 rounded-full bg-[var(--tt-mint-soft)] text-[var(--tt-green)] grid place-items-center"><CheckCircle2 className="h-7 w-7" /></div>
        <h2 className="font-display text-2xl font-bold text-[var(--tt-navy)]">You're subscribed</h2>
        <p className="text-sm text-[var(--tt-muted)]">Your <b>{state.tier}</b> plan is active. Discount will apply to every new shipment.</p>
        <button data-testid="subscribe-success-continue" onClick={() => nav("/portal")} className="btn-primary">Back to dashboard</button>
      </>}
      {state.status === "expired" && <>
        <AlertCircle className="h-10 w-10 mx-auto text-yellow-500" />
        <h2 className="font-display text-2xl font-bold text-[var(--tt-navy)]">Checkout expired</h2>
        <button onClick={() => nav("/portal/subscribe")} className="btn-primary">Try again</button>
      </>}
      {state.status === "timeout" && <>
        <AlertCircle className="h-10 w-10 mx-auto text-yellow-500" />
        <h2 className="font-display text-2xl font-bold text-[var(--tt-navy)]">Still confirming…</h2>
        <p className="text-sm text-[var(--tt-muted)]">Your payment may still be processing. Refresh in a minute or contact support.</p>
        <button onClick={() => nav("/portal")} className="btn-primary">Back to dashboard</button>
      </>}
      {state.status === "error" && <>
        <AlertCircle className="h-10 w-10 mx-auto text-red-500" />
        <h2 className="font-display text-2xl font-bold text-[var(--tt-navy)]">{state.msg}</h2>
        <button onClick={() => nav("/portal/subscribe")} className="btn-primary">Return to plans</button>
      </>}
    </div>
  );
};

export const PortalNewShipment = () => {
  const nav = useNavigate();
  const { customer } = usePortalAuth();
  const [step, setStep] = useState(1); // 1 = addresses, 2 = pick rate, 3 = label + pickup, 4 = done
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ratesResp, setRatesResp] = useState(null);   // {ep_shipment_id, rates}
  const [chosenRate, setChosenRate] = useState(null);
  const [bought, setBought] = useState(null);         // full shipment doc after label buy
  const [pickupResp, setPickupResp] = useState(null); // {pickup_id, pickup_rates}
  const [pickupConfirmed, setPickupConfirmed] = useState(null);

  const [form, setForm] = useState({
    from: { name: customer?.full_name || customer?.company || "", street1: customer?.address_line || "", city: customer?.city || "", state: customer?.state || "", zip: customer?.postal_code || "", country: "US", phone: customer?.phone || "" },
    to: { name: "", street1: "", city: "", state: "", zip: "", country: "US", phone: "" },
    parcel: { length_in: 10, width_in: 8, height_in: 4, weight_oz: 16 },
  });
  const [pickupWindow, setPickupWindow] = useState({ date: "", from: "09:00", to: "17:00", instructions: "" });

  const setFrom = (k, v) => setForm((p) => ({ ...p, from: { ...p.from, [k]: v } }));
  const setTo = (k, v) => setForm((p) => ({ ...p, to: { ...p.to, [k]: v } }));
  const setParcel = (k, v) => setForm((p) => ({ ...p, parcel: { ...p.parcel, [k]: Number(v) || 0 } }));

  // Address book — load saved addresses on mount
  const [senderBook, setSenderBook] = useState([]);
  const [recipientBook, setRecipientBook] = useState([]);
  const [saveSender, setSaveSender] = useState(false);
  const [saveRecipient, setSaveRecipient] = useState(false);
  useEffect(() => {
    portalApi.listAddresses("sender").then((r) => setSenderBook(r.data.addresses || []));
    portalApi.listAddresses("recipient").then((r) => setRecipientBook(r.data.addresses || []));
  }, []);
  const applyAddress = (side, addr) => {
    if (!addr) return;
    setForm((p) => ({ ...p, [side]: {
      name: addr.name || "", street1: addr.street1 || "", street2: addr.street2 || "",
      city: addr.city || "", state: addr.state || "", zip: addr.zip || "",
      country: addr.country || "US", phone: addr.phone || "",
    }}));
  };
  const persistNewAddresses = async () => {
    try {
      if (saveSender) await portalApi.createAddress({ kind: "sender", label: form.from.name || "Sender", ...form.from });
      if (saveRecipient) await portalApi.createAddress({ kind: "recipient", label: form.to.name || "Recipient", ...form.to });
    } catch {}
  };

  const showErr = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return setErr(d);
    if (Array.isArray(d)) return setErr(d.map((x) => `${(x.loc || []).slice(-1)[0] || "field"}: ${x.msg}`).join(" · "));
    setErr(fallback);
  };

  const getRates = async (e) => {
    e?.preventDefault(); setErr(""); setBusy(true);
    // Basic validation
    for (const side of ["from", "to"]) {
      for (const k of ["name", "street1", "city", "zip"]) {
        if (!form[side][k]) { setErr(`Missing "${side} ${k}"`); setBusy(false); return; }
      }
    }
    try {
      const r = await portalApi.getRates({ from_address: form.from, to_address: form.to, parcel: form.parcel });
      setRatesResp(r.data);
      setStep(2);
      persistNewAddresses();
    } catch (e) { showErr(e, "Could not fetch rates"); }
    finally { setBusy(false); }
  };

  const buyLabel = async (rate) => {
    setErr(""); setBusy(true); setChosenRate(rate);
    try {
      const r = await portalApi.buyLabel(ratesResp.ep_shipment_id, rate.id);
      setBought(r.data);
      setStep(3);
    } catch (e) { showErr(e, "Could not purchase label"); setChosenRate(null); }
    finally { setBusy(false); }
  };

  const requestPickup = async (e) => {
    e?.preventDefault(); setErr(""); setBusy(true);
    if (!pickupWindow.date) { setErr("Pickup date required"); setBusy(false); return; }
    const min = new Date(`${pickupWindow.date}T${pickupWindow.from}:00`).toISOString();
    const max = new Date(`${pickupWindow.date}T${pickupWindow.to}:00`).toISOString();
    try {
      const r = await portalApi.createPickup({ ep_shipment_id: bought.ep_shipment_id, min_datetime: min, max_datetime: max, instructions: pickupWindow.instructions });
      setPickupResp(r.data);
      if (r.data.pickup_rates.length === 0) setErr("No carrier can pick up in that window. Try a different date.");
    } catch (e) { showErr(e, "Could not schedule pickup"); }
    finally { setBusy(false); }
  };

  const confirmPickup = async (pickupRate) => {
    setErr(""); setBusy(true);
    try {
      const r = await portalApi.buyPickup({ pickup_id: pickupResp.pickup_id, carrier: pickupRate.carrier, service: pickupRate.service });
      setPickupConfirmed(r.data);
      setStep(4);
    } catch (e) { showErr(e, "Could not confirm pickup"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-[var(--tt-navy)]">New shipment</h1>
        <p className="text-[var(--tt-muted)] mt-1">Live rates from USPS, UPS, FedEx, DHL · buy a label · schedule pickup.</p>
      </div>

      <StepBar step={step} />

      {err && <div className="rounded-lg bg-red-50 text-red-700 text-sm p-3 flex gap-2" data-testid="shipment-error"><AlertCircle className="h-4 w-4 mt-0.5"/>{err}</div>}

      {step === 1 && (
        <form onSubmit={getRates} className="space-y-6">
          <Section title="Ship from (pickup address)" required>
            <AddressPicker items={senderBook} onPick={(a) => applyAddress("from", a)} testid="from-book-select" />
            <div className="grid sm:grid-cols-2 gap-3">
              <Input label="Contact name *" value={form.from.name} onChange={(v) => setFrom("name", v)} testid="ship-from-name" />
              <Input label="Phone" value={form.from.phone} onChange={(v) => setFrom("phone", v)} testid="ship-from-phone" />
              <Input label="Street address *" value={form.from.street1} onChange={(v) => setFrom("street1", v)} testid="ship-from-street" />
              <Input label="City *" value={form.from.city} onChange={(v) => setFrom("city", v)} testid="ship-from-city" />
              <Input label="State" value={form.from.state} onChange={(v) => setFrom("state", v)} testid="ship-from-state" />
              <Input label="ZIP / Postal *" value={form.from.zip} onChange={(v) => setFrom("zip", v)} testid="ship-from-zip" />
            </div>
            <label className="mt-3 flex items-center gap-2 text-xs text-[var(--tt-muted)] cursor-pointer">
              <input type="checkbox" checked={saveSender} onChange={(e) => setSaveSender(e.target.checked)} data-testid="save-sender-cb" />
              Save this sender to my address book
            </label>
          </Section>
          <Section title="Ship to" required>
            <AddressPicker items={recipientBook} onPick={(a) => applyAddress("to", a)} testid="to-book-select" />
            <div className="grid sm:grid-cols-2 gap-3">
              <Input label="Recipient name *" value={form.to.name} onChange={(v) => setTo("name", v)} testid="ship-to-name" />
              <Input label="Recipient phone" value={form.to.phone} onChange={(v) => setTo("phone", v)} testid="ship-to-phone" />
              <Input label="Street address *" value={form.to.street1} onChange={(v) => setTo("street1", v)} testid="ship-to-street" />
              <Input label="City *" value={form.to.city} onChange={(v) => setTo("city", v)} testid="ship-to-city" />
              <Input label="State" value={form.to.state} onChange={(v) => setTo("state", v)} testid="ship-to-state" />
              <Input label="ZIP / Postal *" value={form.to.zip} onChange={(v) => setTo("zip", v)} testid="ship-to-zip" />
            </div>
            <label className="mt-3 flex items-center gap-2 text-xs text-[var(--tt-muted)] cursor-pointer">
              <input type="checkbox" checked={saveRecipient} onChange={(e) => setSaveRecipient(e.target.checked)} data-testid="save-recipient-cb" />
              Save this recipient to my address book
            </label>
          </Section>
          <Section title="Parcel" required>
            <div className="grid sm:grid-cols-4 gap-3">
              <Input label="Length (in) *" value={form.parcel.length_in} onChange={(v) => setParcel("length_in", v)} type="number" testid="parcel-length" />
              <Input label="Width (in) *" value={form.parcel.width_in} onChange={(v) => setParcel("width_in", v)} type="number" testid="parcel-width" />
              <Input label="Height (in) *" value={form.parcel.height_in} onChange={(v) => setParcel("height_in", v)} type="number" testid="parcel-height" />
              <Input label="Weight (oz) *" value={form.parcel.weight_oz} onChange={(v) => setParcel("weight_oz", v)} type="number" testid="parcel-weight" />
            </div>
            <p className="text-xs text-[var(--tt-muted)] mt-2">Tip: 16 oz = 1 lb. Enter weight in ounces.</p>
          </Section>
          <button disabled={busy} data-testid="get-rates-btn" className="btn-primary">{busy ? "Fetching rates…" : "Get live rates"} <ArrowRight className="h-4 w-4" /></button>
        </form>
      )}

      {step === 2 && ratesResp && (
        <div className="space-y-4">
          <div className="text-sm text-[var(--tt-muted)]">Pick a rate — you'll only be charged when you buy the label.</div>
          {ratesResp.rates.length === 0 ? (
            <div className="card p-8 text-center text-[var(--tt-muted)]">No rates returned. Check addresses and parcel dimensions.</div>
          ) : (
            <div className="space-y-2">
              {ratesResp.rates.map((r) => {
                const busyBuy = busy && chosenRate?.id === r.id;
                const discounted = r.discount_pct > 0;
                return (
                  <div key={r.id} className="card p-4 flex items-center justify-between hover:!border-[var(--tt-green)]" data-testid={`rate-row-${r.carrier}-${r.service}`}>
                    <div>
                      <div className="font-semibold text-[var(--tt-navy)]">{r.carrier} · {r.service.replace(/_/g, " ")}</div>
                      <div className="text-xs text-[var(--tt-muted)] mt-0.5">{r.delivery_days ? `${r.delivery_days} business day${r.delivery_days > 1 ? "s" : ""}` : "Delivery time varies"}{r.delivery_date_guaranteed && " · guaranteed"}</div>
                    </div>
                    <div className="text-right">
                      {discounted ? (
                        <>
                          <div className="text-xs text-[var(--tt-muted)] line-through">${r.subtotal.toFixed(2)}</div>
                          <div className="font-display text-lg font-bold text-[var(--tt-green)]">${r.total.toFixed(2)}</div>
                          <div className="text-[10px] uppercase text-[var(--tt-zam-orange)] font-semibold">-{r.discount_pct}% subscription</div>
                        </>
                      ) : (
                        <div className="font-display text-lg font-bold text-[var(--tt-navy)]">${r.total.toFixed(2)}</div>
                      )}
                      <button disabled={busy} onClick={() => buyLabel(r)} data-testid={`buy-rate-${r.carrier}-${r.service}`} className="btn-primary text-xs !py-1.5 !px-3 mt-2">{busyBuy ? "Buying…" : "Buy label"}</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <button onClick={() => { setStep(1); setRatesResp(null); }} className="text-sm text-[var(--tt-muted)] hover:text-[var(--tt-navy)]">← Edit shipment</button>
        </div>
      )}

      {step === 3 && bought && (
        <div className="space-y-4">
          <div className="card p-5 bg-[var(--tt-mint-soft)] border-[var(--tt-mint)]">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[var(--tt-green)] text-white grid place-items-center"><CheckCircle2 className="h-5 w-5" /></div>
              <div>
                <div className="font-semibold text-[var(--tt-navy)]">Label purchased</div>
                <div className="text-xs text-[var(--tt-muted)]">Tracking: <span className="font-mono">{bought.tracking_code}</span> · {bought.carrier} {bought.service?.replace(/_/g, " ")}</div>
              </div>
            </div>
            {bought.label_url && (
              <a href={portalApi.labelUrl(bought.ep_shipment_id)} target="_blank" rel="noreferrer" data-testid="download-label" className="btn-primary mt-4 text-sm">Download label</a>
            )}
          </div>

          <form onSubmit={requestPickup}>
            <Section title="Schedule pickup" required badge="MANDATORY">
              <div className="grid sm:grid-cols-4 gap-3">
                <Input label="Date *" value={pickupWindow.date} onChange={(v) => setPickupWindow((p) => ({ ...p, date: v }))} type="date" testid="pickup-date" />
                <Input label="Earliest *" value={pickupWindow.from} onChange={(v) => setPickupWindow((p) => ({ ...p, from: v }))} type="time" testid="pickup-from" />
                <Input label="Latest *" value={pickupWindow.to} onChange={(v) => setPickupWindow((p) => ({ ...p, to: v }))} type="time" testid="pickup-to" />
                <Input label="Instructions" value={pickupWindow.instructions} onChange={(v) => setPickupWindow((p) => ({ ...p, instructions: v }))} testid="pickup-notes" />
              </div>
              <button disabled={busy} data-testid="request-pickup-btn" className="btn-primary mt-4 text-sm">{busy ? "Checking carriers…" : "Get pickup options"}</button>
            </Section>
          </form>

          {pickupResp && pickupResp.pickup_rates.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-widest text-[var(--tt-muted)] font-semibold">Choose a carrier</div>
              {pickupResp.pickup_rates.map((p, i) => (
                <div key={i} className="card p-4 flex items-center justify-between" data-testid={`pickup-rate-${p.carrier}`}>
                  <div>
                    <div className="font-semibold text-[var(--tt-navy)]">{p.carrier} · {p.service}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-lg font-bold text-[var(--tt-navy)]">${p.rate.toFixed(2)}</div>
                    <button disabled={busy} onClick={() => confirmPickup(p)} className="btn-primary text-xs !py-1.5 !px-3 mt-2" data-testid={`confirm-pickup-${p.carrier}`}>{busy ? "…" : "Confirm"}</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="card p-8 text-center space-y-4" data-testid="shipment-done">
          <div className="mx-auto h-14 w-14 rounded-full bg-[var(--tt-mint-soft)] text-[var(--tt-green)] grid place-items-center"><CheckCircle2 className="h-7 w-7" /></div>
          <h2 className="font-display text-2xl font-bold text-[var(--tt-navy)]">Pickup scheduled</h2>
          <div className="text-sm text-[var(--tt-muted)]">Confirmation: <span className="font-mono text-[var(--tt-navy)]">{pickupConfirmed?.confirmation || "—"}</span></div>
          <div className="text-sm">Tracking: <span className="font-mono text-[var(--tt-navy)]">{bought?.tracking_code}</span></div>
          {bought?.label_url && <a href={portalApi.labelUrl(bought.ep_shipment_id)} target="_blank" rel="noreferrer" className="btn-primary text-sm inline-flex">Download label</a>}
          <div><button onClick={() => nav("/portal")} className="text-sm text-[var(--tt-muted)] hover:text-[var(--tt-navy)]">Back to dashboard</button></div>
        </div>
      )}
    </div>
  );
};

const StepBar = ({ step }) => (
  <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
    {[["Addresses & parcel", 1], ["Pick rate", 2], ["Label + pickup", 3], ["Done", 4]].map(([label, n]) => (
      <div key={n} className="flex items-center gap-2">
        <div className={`h-6 w-6 rounded-full grid place-items-center font-bold ${step >= n ? "bg-[var(--tt-green)] text-white" : "bg-white border border-[var(--tt-line)] text-[var(--tt-muted)]"}`}>{n}</div>
        <span className={step >= n ? "text-[var(--tt-navy)] font-semibold" : "text-[var(--tt-muted)]"}>{label}</span>
        {n < 4 && <span className="text-[var(--tt-muted)]">›</span>}
      </div>
    ))}
  </div>
);

const Section = ({ title, required, badge, children }) => (
  <fieldset className="card p-5">
    <legend className="px-2 text-sm font-semibold text-[var(--tt-navy)] flex items-center gap-2">{title}{required && <span className="text-red-500">*</span>}{badge && <span className="text-[10px] uppercase tracking-widest bg-[var(--tt-zam-orange)] text-white rounded-full px-2 py-0.5">{badge}</span>}</legend>
    <div className="mt-3">{children}</div>
  </fieldset>
);

const Input = ({ label, value, onChange, type = "text", testid }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider text-[var(--tt-muted)]">{label}</span>
    <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-xl border border-[var(--tt-line)] bg-white px-3 py-2.5 text-sm outline-none focus:border-[var(--tt-green)] transition" />
  </label>
);


// ─── Bulk CSV Shipping (Business only) ───────────────────────────────────────
const CSV_HEADERS = "to_name,to_street1,to_city,to_state,to_zip,to_country,to_phone,length_in,width_in,height_in,weight_oz";
const CSV_SAMPLE = `${CSV_HEADERS}
Jane Doe,179 N Harbor Dr,Redondo Beach,CA,90277,US,3105551212,10,8,4,16
John Smith,500 5th Ave,New York,NY,10110,US,2125551313,12,10,6,32
Ada Ng,42 Wallaby Way,Sydney,,2000,AU,,10,8,4,20`;

export const PortalBulk = () => {
  const { customer } = usePortalAuth();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [file, setFile] = useState(null);
  const [fromAddr, setFromAddr] = useState({
    name: customer?.company || customer?.full_name || "",
    street1: customer?.address_line || "", city: customer?.city || "", state: customer?.state || "",
    zip: customer?.postal_code || "", country: "US", phone: customer?.phone || "",
  });
  const [batch, setBatch] = useState(null);       // uploaded batch
  const [rated, setRated] = useState(null);       // batch with rates
  const [mode, setMode] = useState("auto_cheapest"); // or "manual"
  const [rowChoices, setRowChoices] = useState({}); // { row_id: rate_id }
  const [pickupWindow, setPickupWindow] = useState({ date: "", from: "09:00", to: "17:00", instructions: "" });
  const [pickupResp, setPickupResp] = useState(null);

  if (customer?.account_type !== "business") {
    return (
      <div className="card p-8 text-center">
        <Layers className="h-8 w-8 mx-auto text-[var(--tt-muted)]" />
        <h2 className="font-display text-2xl font-bold text-[var(--tt-navy)] mt-2">Bulk shipping is a Business feature</h2>
        <p className="text-sm text-[var(--tt-muted)] mt-2">Register a Business account to unlock CSV bulk shipping and multi-user seats.</p>
      </div>
    );
  }

  const showErr = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") setErr(d);
    else if (Array.isArray(d)) setErr(d.map((x) => `${(x.loc || []).slice(-1)[0] || "field"}: ${x.msg}`).join(" · "));
    else setErr(fallback);
  };

  const downloadSample = () => {
    const blob = new Blob([CSV_SAMPLE], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "turbo-bulk-template.csv"; a.click();
    URL.revokeObjectURL(a.href);
  };

  const upload = async (e) => {
    e?.preventDefault(); setErr(""); setBusy(true);
    if (!file) { setErr("Choose a CSV file"); setBusy(false); return; }
    for (const k of ["name", "street1", "city", "zip"]) if (!fromAddr[k]) { setErr(`Sender ${k} required`); setBusy(false); return; }
    try {
      const r = await portalApi.bulkUpload(file, fromAddr);
      setBatch(r.data); setStep(2);
    } catch (e) { showErr(e, "Upload failed"); }
    finally { setBusy(false); }
  };

  const rateAll = async () => {
    setErr(""); setBusy(true);
    try {
      const r = await portalApi.bulkRate(batch.id);
      setRated(r.data);
      const choices = {};
      for (const row of r.data.rows || []) if (row.cheapest_rate_id) choices[row.id] = row.cheapest_rate_id;
      setRowChoices(choices);
      setStep(3);
    } catch (e) { showErr(e, "Rate fetch failed"); }
    finally { setBusy(false); }
  };

  const buyAll = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = mode === "manual" ? { mode: "manual", row_choices: rowChoices } : { mode: "auto_cheapest" };
      const r = await portalApi.bulkBuy(batch.id, payload);
      setRated(r.data); setStep(4);
    } catch (e) { showErr(e, "Buying labels failed"); }
    finally { setBusy(false); }
  };

  const requestPickup = async (e) => {
    e?.preventDefault(); setErr(""); setBusy(true);
    if (!pickupWindow.date) { setErr("Pickup date required"); setBusy(false); return; }
    const min = new Date(`${pickupWindow.date}T${pickupWindow.from}:00`).toISOString();
    const max = new Date(`${pickupWindow.date}T${pickupWindow.to}:00`).toISOString();
    try {
      const r = await portalApi.bulkPickup(batch.id, { min_datetime: min, max_datetime: max, instructions: pickupWindow.instructions });
      setPickupResp(r.data);
    } catch (e) { showErr(e, "Could not schedule pickup"); }
    finally { setBusy(false); }
  };

  const boughtCount = (rated?.rows || []).filter((r) => r.status === "bought").length;
  const failedCount = (rated?.rows || []).filter((r) => r.status === "failed").length;
  const totalSpent = (rated?.rows || []).reduce((s, r) => s + (r.status === "bought" ? r.total : 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-[var(--tt-navy)]">Bulk shipping</h1>
        <p className="text-[var(--tt-muted)] mt-1">Upload a CSV · rate-shop every row · buy labels · schedule one pickup for the batch.</p>
      </div>

      <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
        {[["Upload", 1], ["Preview", 2], ["Pick rates", 3], ["Bought", 4]].map(([label, n]) => (
          <div key={n} className="flex items-center gap-2">
            <div className={`h-6 w-6 rounded-full grid place-items-center font-bold ${step >= n ? "bg-[var(--tt-green)] text-white" : "bg-white border border-[var(--tt-line)] text-[var(--tt-muted)]"}`}>{n}</div>
            <span className={step >= n ? "text-[var(--tt-navy)] font-semibold" : "text-[var(--tt-muted)]"}>{label}</span>
            {n < 4 && <span className="text-[var(--tt-muted)]">›</span>}
          </div>
        ))}
      </div>

      {err && <div className="rounded-lg bg-red-50 text-red-700 text-sm p-3 flex gap-2" data-testid="bulk-error"><AlertCircle className="h-4 w-4 mt-0.5"/>{err}</div>}

      {step === 1 && (
        <form onSubmit={upload} className="space-y-6">
          <Section title="Sender (pickup address for the batch)" required>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input label="Company / Name *" value={fromAddr.name} onChange={(v) => setFromAddr({ ...fromAddr, name: v })} testid="bulk-from-name" />
              <Input label="Phone" value={fromAddr.phone} onChange={(v) => setFromAddr({ ...fromAddr, phone: v })} testid="bulk-from-phone" />
              <Input label="Street *" value={fromAddr.street1} onChange={(v) => setFromAddr({ ...fromAddr, street1: v })} testid="bulk-from-street" />
              <Input label="City *" value={fromAddr.city} onChange={(v) => setFromAddr({ ...fromAddr, city: v })} testid="bulk-from-city" />
              <Input label="State" value={fromAddr.state} onChange={(v) => setFromAddr({ ...fromAddr, state: v })} testid="bulk-from-state" />
              <Input label="ZIP *" value={fromAddr.zip} onChange={(v) => setFromAddr({ ...fromAddr, zip: v })} testid="bulk-from-zip" />
            </div>
          </Section>
          <Section title="CSV file" required>
            <div className="flex items-center gap-3">
              <label className="btn-primary text-sm cursor-pointer" data-testid="bulk-file-label">
                <Upload className="h-4 w-4" /> Choose CSV
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0])} data-testid="bulk-file-input" />
              </label>
              <span className="text-sm text-[var(--tt-muted)]" data-testid="bulk-file-name">{file?.name || "no file chosen"}</span>
              <button type="button" onClick={downloadSample} className="ml-auto text-xs text-[var(--tt-green)] hover:underline" data-testid="bulk-download-sample"><FileDown className="h-3 w-3 inline mr-1" />Download sample CSV</button>
            </div>
            <div className="mt-4 text-xs text-[var(--tt-muted)]">
              <div className="font-semibold uppercase tracking-widest mb-1">Required columns</div>
              <code className="text-[11px] block bg-[var(--tt-mint-soft)] p-2 rounded-lg overflow-x-auto">{CSV_HEADERS}</code>
            </div>
          </Section>
          <button disabled={busy} data-testid="bulk-upload-btn" className="btn-primary">{busy ? "Uploading…" : "Upload & preview"} <ArrowRight className="h-4 w-4" /></button>
        </form>
      )}

      {step === 2 && batch && (
        <div className="space-y-4">
          <div className="card p-4 text-sm flex items-center justify-between">
            <div><b>{batch.rows.length}</b> rows parsed · <span className="text-red-600">{batch.parse_errors.length} errors</span></div>
            <button disabled={busy} onClick={rateAll} data-testid="bulk-rate-btn" className="btn-primary text-sm">{busy ? "Rating…" : "Rate-shop every row"}</button>
          </div>
          {batch.parse_errors.length > 0 && (
            <div className="card p-4 text-xs">
              <div className="font-semibold mb-1">Skipped rows</div>
              {batch.parse_errors.slice(0, 5).map((e, i) => <div key={i}>Line {e.line}: {e.error}</div>)}
              {batch.parse_errors.length > 5 && <div className="text-[var(--tt-muted)]">+ {batch.parse_errors.length - 5} more</div>}
            </div>
          )}
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-widest text-[var(--tt-muted)]"><tr><th className="p-3">Line</th><th className="p-3">Recipient</th><th className="p-3">Destination</th><th className="p-3">Parcel</th></tr></thead>
              <tbody>
                {batch.rows.slice(0, 100).map((r) => (
                  <tr key={r.id} className="border-t border-[var(--tt-line)]">
                    <td className="p-3 text-xs text-[var(--tt-muted)]">{r.line}</td>
                    <td className="p-3">{r.to_address.name}</td>
                    <td className="p-3 text-[var(--tt-muted)]">{r.to_address.city}, {r.to_address.state} {r.to_address.zip} {r.to_address.country}</td>
                    <td className="p-3 text-xs text-[var(--tt-muted)]">{r.parcel.length_in}×{r.parcel.width_in}×{r.parcel.height_in}in · {r.parcel.weight_oz}oz</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {step === 3 && rated && (
        <div className="space-y-4">
          <div className="card p-4 flex flex-wrap gap-3 items-center justify-between">
            <div className="flex gap-4 items-center text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={mode === "auto_cheapest"} onChange={() => setMode("auto_cheapest")} data-testid="bulk-mode-auto" />
                <span><b>Auto-buy cheapest</b> for every row</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={mode === "manual"} onChange={() => setMode("manual")} data-testid="bulk-mode-manual" />
                <span>Review rates per row</span>
              </label>
            </div>
            <button disabled={busy} onClick={buyAll} data-testid="bulk-buy-btn" className="btn-primary text-sm">{busy ? "Buying…" : `Buy labels · est $${rated.rows.filter(r => r.rates_snapshot?.length).reduce((s, r) => { const rt = rowChoices[r.id] ? r.rates_snapshot.find(x => x.id === rowChoices[r.id]) : r.rates_snapshot[0]; return s + (rt?.total || 0); }, 0).toFixed(2)}`} <DollarSign className="h-4 w-4" /></button>
          </div>
          <div className="space-y-2">
            {rated.rows.map((r) => (
              <div key={r.id} className="card p-4" data-testid={`bulk-row-${r.line}`}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-xs text-[var(--tt-muted)]">Line {r.line}</div>
                    <div className="font-semibold text-[var(--tt-navy)]">{r.to_address.name}</div>
                    <div className="text-xs text-[var(--tt-muted)]">{r.to_address.city}, {r.to_address.state} {r.to_address.zip}</div>
                  </div>
                  {r.status === "failed" ? <div className="text-xs text-red-600 max-w-xs text-right">{r.error}</div>
                    : mode === "auto_cheapest" ? (
                      <div className="text-right">
                        <div className="font-display text-lg font-bold text-[var(--tt-green)]">${(r.rates_snapshot?.[0]?.total || 0).toFixed(2)}</div>
                        <div className="text-xs text-[var(--tt-muted)]">{r.rates_snapshot?.[0]?.carrier} · {r.rates_snapshot?.[0]?.service?.replace(/_/g, " ")}</div>
                      </div>
                    ) : (
                      <select data-testid={`bulk-row-select-${r.line}`} value={rowChoices[r.id] || ""} onChange={(e) => setRowChoices({ ...rowChoices, [r.id]: e.target.value })} className="rounded-lg border border-[var(--tt-line)] bg-white px-3 py-2 text-sm max-w-xs">
                        {(r.rates_snapshot || []).map((rt) => <option key={rt.id} value={rt.id}>{rt.carrier} {rt.service?.replace(/_/g, " ")} — ${rt.total.toFixed(2)}</option>)}
                      </select>
                    )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 4 && rated && (
        <div className="space-y-4">
          <div className="card p-5 bg-[var(--tt-mint-soft)] border-[var(--tt-mint)] flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-display text-2xl font-bold text-[var(--tt-navy)]">Batch complete</div>
              <div className="text-sm text-[var(--tt-muted)]"><b>{boughtCount}</b> labels bought · <b className="text-red-600">{failedCount}</b> failed · total <b>${totalSpent.toFixed(2)}</b></div>
            </div>
            <a href={portalApi.bulkZipUrl(batch.id)} target="_blank" rel="noreferrer" className="btn-primary text-sm" data-testid="bulk-zip-download"><FileDown className="h-4 w-4" /> Download labels (.zip)</a>
          </div>

          <form onSubmit={requestPickup}>
            <Section title="Schedule one pickup for the batch" required badge="OPTIONAL">
              <div className="grid sm:grid-cols-4 gap-3">
                <Input label="Date *" value={pickupWindow.date} onChange={(v) => setPickupWindow({ ...pickupWindow, date: v })} type="date" testid="bulk-pickup-date" />
                <Input label="Earliest" value={pickupWindow.from} onChange={(v) => setPickupWindow({ ...pickupWindow, from: v })} type="time" testid="bulk-pickup-from" />
                <Input label="Latest" value={pickupWindow.to} onChange={(v) => setPickupWindow({ ...pickupWindow, to: v })} type="time" testid="bulk-pickup-to" />
                <Input label="Instructions" value={pickupWindow.instructions} onChange={(v) => setPickupWindow({ ...pickupWindow, instructions: v })} testid="bulk-pickup-notes" />
              </div>
              <button disabled={busy} data-testid="bulk-pickup-btn" className="btn-primary mt-4 text-sm">{busy ? "Checking…" : "Get pickup options"}</button>
            </Section>
          </form>

          {pickupResp && (
            <div className="card p-4">
              <div className="text-xs uppercase tracking-widest text-[var(--tt-muted)] mb-2">Pickup carriers</div>
              {pickupResp.pickup_rates.length === 0
                ? <div className="text-sm text-[var(--tt-muted)]">No carrier can pick up in that window. Try a different date.</div>
                : <div className="space-y-2">{pickupResp.pickup_rates.map((p, i) => <div key={i} className="flex items-center justify-between"><div>{p.carrier} · {p.service}</div><div className="font-semibold">${p.rate.toFixed(2)}</div></div>)}</div>}
            </div>
          )}

          <div className="card p-4">
            <div className="text-xs uppercase tracking-widest text-[var(--tt-muted)] mb-2">Rows</div>
            <div className="space-y-1 text-sm">
              {rated.rows.map((r) => (
                <div key={r.id} className="flex items-center justify-between border-b border-[var(--tt-line)] py-2 last:border-b-0">
                  <div className="truncate">
                    <span className="font-mono text-xs text-[var(--tt-muted)] mr-2">L{r.line}</span>
                    <span className="text-[var(--tt-navy)]">{r.to_address.name}</span>
                    <span className="text-[var(--tt-muted)] text-xs"> → {r.to_address.city}, {r.to_address.state}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                              {r.status === "bought" ? (
                      <>
                        <span className="font-mono text-[var(--tt-muted)]">{r.tracking_code}</span>
                        <span className="font-semibold text-[var(--tt-navy)]">${(r.total || 0).toFixed(2)}</span>
                        {r.ep_shipment_id && <a href={portalApi.labelUrl(r.ep_shipment_id)} target="_blank" rel="noreferrer" className="text-[var(--tt-green)] hover:underline">Label</a>}
                      </>
                    ) : <span className="text-red-600">{r.error || r.status}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Address book helpers ────────────────────────────────────────────────────
const AddressPicker = ({ items, onPick, testid }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-3 flex items-center gap-2">
      <span className="text-[11px] uppercase tracking-widest text-[var(--tt-muted)]">Use saved</span>
      <select data-testid={testid} defaultValue="" onChange={(e) => {
        const a = items.find((x) => x.id === e.target.value);
        onPick(a); e.target.value = "";
      }} className="rounded-lg border border-[var(--tt-line)] bg-white px-3 py-1.5 text-sm max-w-[240px]">
        <option value="" disabled>Pick from address book…</option>
        {items.map((a) => (
          <option key={a.id} value={a.id}>{a.label || a.name} — {a.city}{a.state ? `, ${a.state}` : ""}{a.is_default ? " ★" : ""}</option>
        ))}
      </select>
    </div>
  );
};

const EMPTY_ADDR = { kind: "recipient", label: "", name: "", street1: "", street2: "", city: "", state: "", zip: "", country: "US", phone: "", email: "", is_default: false };

export const PortalAddresses = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = () => portalApi.listAddresses().then((r) => setItems(r.data.addresses || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      if (editing.id) await portalApi.updateAddress(editing.id, editing);
      else await portalApi.createAddress(editing);
      setEditing(null); await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(Array.isArray(d) ? d.map(x => x.msg).join(", ") : (d || "Could not save"));
    } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    await portalApi.deleteAddress(id); await load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--tt-navy)]">Address book</h1>
          <p className="text-[var(--tt-muted)] mt-1">Save frequent senders and recipients — one-click autofill on every shipment.</p>
        </div>
        <button data-testid="address-new" onClick={() => setEditing({ ...EMPTY_ADDR })} className="btn-primary text-sm">+ Add address</button>
      </div>

      {items.length === 0 ? (
        <div className="card p-10 text-center">
          <BookOpen className="h-8 w-8 mx-auto text-[var(--tt-muted)]" />
          <p className="mt-3 text-sm text-[var(--tt-muted)]">No saved addresses yet.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((a) => (
            <div key={a.id} className="card p-4" data-testid={`address-row-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--tt-zam-orange)] font-semibold">{a.kind}{a.is_default && <span className="ml-2 text-[var(--tt-green)]"><Star className="h-3 w-3 inline" /> default</span>}</div>
                  <div className="font-semibold text-[var(--tt-navy)] mt-1">{a.label || a.name}</div>
                  <div className="text-sm text-[var(--tt-ink-soft)]">{a.name}</div>
                  <div className="text-sm text-[var(--tt-muted)]">{a.street1}{a.street2 ? `, ${a.street2}` : ""}</div>
                  <div className="text-sm text-[var(--tt-muted)]">{a.city}{a.state ? `, ${a.state}` : ""} {a.zip} · {a.country}</div>
                  {a.phone && <div className="text-xs text-[var(--tt-muted)] mt-1">{a.phone}</div>}
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => setEditing({ ...a })} data-testid={`address-edit-${a.id}`} className="text-xs text-[var(--tt-green)] hover:underline">Edit</button>
                  <button onClick={() => del(a.id)} data-testid={`address-delete-${a.id}`} className="text-xs text-red-600 hover:underline">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => !busy && setEditing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-xl p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="address-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-[var(--tt-navy)]">{editing.id ? "Edit address" : "New address"}</h3>
              <button onClick={() => setEditing(null)} className="text-[var(--tt-muted)] hover:text-[var(--tt-navy)]"><X className="h-5 w-5" /></button>
            </div>
            {err && <div className="rounded-lg bg-red-50 text-red-700 text-sm p-2 flex gap-2"><AlertCircle className="h-4 w-4 mt-0.5"/>{err}</div>}
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-[var(--tt-muted)]">Kind</span>
                <select value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })} data-testid="address-kind" className="mt-1 w-full rounded-xl border border-[var(--tt-line)] bg-white px-3 py-2.5 text-sm">
                  <option value="sender">Sender (my pickup)</option>
                  <option value="recipient">Recipient</option>
                  <option value="both">Both</option>
                </select>
              </label>
              <Input label="Nickname (optional)" value={editing.label} onChange={(v) => setEditing({ ...editing, label: v })} testid="address-label" />
              <Input label="Name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="address-name" />
              <Input label="Phone" value={editing.phone} onChange={(v) => setEditing({ ...editing, phone: v })} testid="address-phone" />
              <Input label="Street *" value={editing.street1} onChange={(v) => setEditing({ ...editing, street1: v })} testid="address-street1" />
              <Input label="Apt / Suite" value={editing.street2} onChange={(v) => setEditing({ ...editing, street2: v })} testid="address-street2" />
              <Input label="City *" value={editing.city} onChange={(v) => setEditing({ ...editing, city: v })} testid="address-city" />
              <Input label="State" value={editing.state} onChange={(v) => setEditing({ ...editing, state: v })} testid="address-state" />
              <Input label="ZIP *" value={editing.zip} onChange={(v) => setEditing({ ...editing, zip: v })} testid="address-zip" />
              <Input label="Country (ISO-2)" value={editing.country} onChange={(v) => setEditing({ ...editing, country: v.toUpperCase() })} testid="address-country" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={editing.is_default} onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })} data-testid="address-default" />
              <Star className="h-4 w-4 text-[var(--tt-green)]" /> Default for this kind
            </label>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditing(null)} className="text-sm text-[var(--tt-muted)] hover:text-[var(--tt-navy)] px-4">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="address-save" className="btn-primary text-sm">{busy ? "Saving…" : "Save address"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Return Label modal ──────────────────────────────────────────────────────
export const ReturnLabelModal = ({ shipment, onClose, onDone }) => {
  const [step, setStep] = useState(0); // 0 = loading rates, 1 = pick rate, 2 = success
  const [err, setErr] = useState("");
  const [rates, setRates] = useState([]);
  const [bought, setBought] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chosen, setChosen] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await portalApi.returnRates(shipment.ep_shipment_id);
        if (cancelled) return;
        setRates(r.data.rates || []); setStep(1);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not fetch return rates");
      }
    })();
    return () => { cancelled = true; };
  }, [shipment.ep_shipment_id]);

  const buy = async (r) => {
    setBusy(true); setErr(""); setChosen(r);
    try {
      const resp = await portalApi.buyReturn(shipment.ep_shipment_id, r.id);
      setBought(resp.data); setStep(2); onDone?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not buy return label"); setChosen(null);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => !busy && onClose()}>
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="return-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-[var(--tt-navy)]">Request a return label</h3>
          <button onClick={onClose} className="text-[var(--tt-muted)] hover:text-[var(--tt-navy)]"><X className="h-5 w-5" /></button>
        </div>
        <div className="text-xs text-[var(--tt-muted)]">
          Return path: <b>{shipment.to_address?.name}</b> → <b>{shipment.from_address?.name}</b>
        </div>
        {err && <div className="rounded-lg bg-red-50 text-red-700 text-sm p-2 flex gap-2"><AlertCircle className="h-4 w-4 mt-0.5"/>{err}</div>}
        {step === 0 && !err && (
          <div className="text-center py-8">
            <div className="mx-auto h-10 w-10 rounded-full border-4 border-[var(--tt-green)] border-t-transparent animate-spin" />
            <p className="mt-3 text-sm text-[var(--tt-muted)]">Fetching return rates…</p>
          </div>
        )}
        {step === 1 && (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {rates.length === 0 && <div className="text-sm text-[var(--tt-muted)] text-center py-4">No return rates available for this shipment.</div>}
            {rates.slice(0, 8).map((r) => (
              <div key={r.id} className="card p-3 flex items-center justify-between" data-testid={`return-rate-${r.carrier}-${r.service}`}>
                <div>
                  <div className="font-semibold text-[var(--tt-navy)] text-sm">{r.carrier} · {r.service?.replace(/_/g, " ")}</div>
                  <div className="text-xs text-[var(--tt-muted)]">{r.delivery_days ? `${r.delivery_days} business day${r.delivery_days > 1 ? "s" : ""}` : ""}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-base font-bold text-[var(--tt-navy)]">${r.total.toFixed(2)}</div>
                  <button onClick={() => buy(r)} disabled={busy} data-testid={`return-buy-${r.carrier}-${r.service}`} className="btn-primary text-xs !py-1 !px-2 mt-1">{busy && chosen?.id === r.id ? "…" : "Buy"}</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {step === 2 && bought && (
          <div className="text-center py-4 space-y-3">
            <div className="mx-auto h-12 w-12 rounded-full bg-[var(--tt-mint-soft)] text-[var(--tt-green)] grid place-items-center"><CheckCircle2 className="h-6 w-6" /></div>
            <div className="font-display text-lg font-bold text-[var(--tt-navy)]">Return label purchased</div>
            <div className="text-xs text-[var(--tt-muted)]">Tracking: <span className="font-mono">{bought.return_tracking_code}</span></div>
            <a href={portalApi.returnLabelUrl(shipment.ep_shipment_id)} target="_blank" rel="noreferrer" data-testid="return-download" className="btn-primary text-sm inline-flex">Download return label</a>
          </div>
        )}
      </div>
    </div>
  );
};


// ─── Team (Multi-user seats, Business only) ──────────────────────────────────
export const PortalTeam = () => {
  const { customer } = usePortalAuth();
  const [data, setData] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");

  const load = () => portalApi.team().then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Not a business account"));
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault(); setErr(""); setNotice(""); setInviting(true);
    try {
      const r = await portalApi.inviteMember(inviteEmail.trim().toLowerCase());
      setInviteEmail("");
      const inv = r.data.invite;
      const link = `${window.location.origin}/portal/join?token=${inv.token}`;
      // Show delivery status
      const emailOk = r.data.email_delivery && !r.data.email_delivery.error;
      setNotice(emailOk
        ? `Invite emailed to ${inv.email}. Direct link (share manually if needed): ${link}`
        : `Invite created but email failed (${(r.data.email_delivery && r.data.email_delivery.error) || "domain not verified"}). Send this link manually: ${link}`);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(Array.isArray(d) ? d.map(x => x.msg).join(", ") : (d || "Could not invite"));
    } finally { setInviting(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this invite?")) return;
    await portalApi.cancelInvite(id); await load();
  };
  const remove = async (cid, email) => {
    if (!window.confirm(`Remove ${email} from your team? They will lose access to team shipments.`)) return;
    await portalApi.removeMember(cid); await load();
  };
  const copyLink = (token) => {
    const link = `${window.location.origin}/portal/join?token=${token}`;
    navigator.clipboard?.writeText(link);
    setNotice(`Link copied: ${link}`);
  };

  if (customer?.account_type !== "business") {
    return (
      <div className="card p-8 text-center">
        <Users className="h-8 w-8 mx-auto text-[var(--tt-muted)]" />
        <h2 className="font-display text-2xl font-bold text-[var(--tt-navy)] mt-2">Team is a Business feature</h2>
        <p className="text-sm text-[var(--tt-muted)] mt-2">Upgrade to a business account to invite teammates.</p>
      </div>
    );
  }

  const isOwner = data?.role === "owner";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-[var(--tt-navy)]">Team</h1>
        <p className="text-[var(--tt-muted)] mt-1">Invite teammates to share this shipping console. Everyone sees the same shipments and address book.</p>
      </div>

      {isOwner && (
        <Section title="Invite a teammate" required badge="OWNER">
          <form onSubmit={invite} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[240px]">
              <Input label="Email address *" value={inviteEmail} onChange={setInviteEmail} type="email" testid="team-invite-email" />
            </div>
            <button disabled={inviting || !inviteEmail} data-testid="team-invite-submit" className="btn-primary text-sm">{inviting ? "Sending…" : "Send invite"} <Send className="h-4 w-4" /></button>
          </form>
          {err && <div className="mt-3 rounded-lg bg-red-50 text-red-700 text-sm p-2" data-testid="team-invite-error">{err}</div>}
          {notice && <div className="mt-3 rounded-lg bg-[var(--tt-mint-soft)] text-[var(--tt-navy)] text-xs p-3 break-all" data-testid="team-invite-notice">{notice}</div>}
        </Section>
      )}

      <div>
        <div className="text-xs uppercase tracking-widest text-[var(--tt-muted)] font-semibold mb-3">Members ({data?.members?.length || 0})</div>
        <div className="space-y-2">
          {(data?.members || []).map((m) => (
            <div key={m.id} className="card p-4 flex items-center justify-between" data-testid={`team-member-${m.id}`}>
              <div>
                <div className="font-semibold text-[var(--tt-navy)]">{m.full_name || m.email}</div>
                <div className="text-xs text-[var(--tt-muted)]">{m.email}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-[10px] uppercase tracking-widest font-semibold ${m.role === "owner" ? "text-[var(--tt-zam-orange)]" : "text-[var(--tt-green)]"}`}>{m.role === "owner" ? <><ShieldCheck className="h-3 w-3 inline mr-0.5" /> owner</> : m.role || "member"}</span>
                {isOwner && m.id !== customer.id && (
                  <>
                    <button onClick={async () => { if (window.confirm(`Make ${m.email} the owner? You will become a regular member.`)) { await portalApi.transferOwnership(m.id); await load(); } }} data-testid={`team-transfer-${m.id}`} className="text-xs text-[var(--tt-zam-orange)] hover:underline">Make owner</button>
                    <button onClick={() => remove(m.id, m.email)} data-testid={`team-remove-${m.id}`} className="text-xs text-red-600 hover:underline">Remove</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {isOwner && (data?.invites || []).length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--tt-muted)] font-semibold mb-3">Pending invites ({data.invites.length})</div>
          <div className="space-y-2">
            {data.invites.map((inv) => (
              <div key={inv.id} className="card p-4 flex items-center justify-between" data-testid={`team-invite-${inv.id}`}>
                <div>
                  <div className="font-semibold text-[var(--tt-navy)]">{inv.email}</div>
                  <div className="text-xs text-[var(--tt-muted)]">Sent {new Date(inv.invited_at).toLocaleDateString()} · expires {new Date(inv.expires_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => copyLink(inv.token)} data-testid={`team-invite-copy-${inv.id}`} className="text-xs text-[var(--tt-green)] hover:underline flex items-center gap-1"><Copy className="h-3 w-3" /> Copy link</button>
                  <button onClick={() => cancel(inv.id)} data-testid={`team-invite-cancel-${inv.id}`} className="text-xs text-red-600 hover:underline">Cancel</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── /portal/join?token=... (invite acceptance page) ─────────────────────────
export const PortalJoin = () => {
  const nav = useNavigate();
  const { customer, setCustomer } = usePortalAuth();
  const token = new URLSearchParams(window.location.search).get("token");
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setErr("Missing invite token"); return; }
    portalApi.lookupInvite(token).then((r) => setInvite(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Invite not found"));
  }, [token]);

  const accept = async () => {
    setErr(""); setBusy(true);
    try {
      const r = await portalApi.acceptInvite(token);
      setCustomer(r.data.customer);
      nav("/portal");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not accept invite");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6 bg-[var(--tt-cream)]">
      <div className="card p-8 max-w-md w-full space-y-4" data-testid="join-card">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-[var(--tt-green)] text-white grid place-items-center"><Users className="h-5 w-5" /></div>
          <h1 className="font-display text-2xl font-bold text-[var(--tt-navy)]">Team invitation</h1>
        </div>
        {err && <div className="rounded-lg bg-red-50 text-red-700 text-sm p-3 flex gap-2"><AlertCircle className="h-4 w-4 mt-0.5"/>{err}</div>}
        {!invite && !err && <div className="text-sm text-[var(--tt-muted)]">Loading invite…</div>}
        {invite && (
          <>
            <div className="text-sm text-[var(--tt-ink-soft)]">
              <b>{invite.invited_by}</b> invited you to join <b>{invite.organization.name}</b> on Turbo Transit.
            </div>
            <div className="rounded-lg bg-[var(--tt-mint-soft)] p-3 text-xs">
              This invite is for <b>{invite.email}</b> · role <b>{invite.role}</b>
            </div>
            {customer ? (
              customer.email.toLowerCase() === invite.email.toLowerCase() ? (
                <button onClick={accept} disabled={busy} data-testid="join-accept" className="btn-primary w-full justify-center">{busy ? "Joining…" : `Accept & join ${invite.organization.name}`}</button>
              ) : (
                <div className="text-sm text-[var(--tt-muted)]">You're signed in as <b>{customer.email}</b> but this invite is for <b>{invite.email}</b>. Please <a href="/portal/logout" className="text-[var(--tt-green)] hover:underline">sign out</a> and register with the invited email.</div>
              )
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-[var(--tt-muted)]">Sign in or create an account with <b>{invite.email}</b> to accept.</p>
                <a href={`/portal/register?invite=${token}`} data-testid="join-register-cta" className="btn-primary w-full justify-center">Create account & join</a>
                <a href="/portal/login" className="block text-center text-sm text-[var(--tt-green)] hover:underline">I already have an account</a>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};


// ─── Delivery Email Templates editor ─────────────────────────────────────────
const STATUS_LABELS = {
  in_transit: { label: "In transit", desc: "Sent when the parcel gets its first carrier scan" },
  out_for_delivery: { label: "Out for delivery", desc: "Sent when the carrier marks it out for delivery today" },
  delivered: { label: "Delivered", desc: "Sent when the parcel is confirmed delivered" },
};
const TONE_OPTIONS = ["friendly", "professional", "warm", "playful", "concise"];

export const PortalTemplates = () => {
  const { customer } = usePortalAuth();
  const [templates, setTemplates] = useState(null);
  const [vars, setVars] = useState([]);
  const [previews, setPreviews] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");
  const [aiBusy, setAiBusy] = useState("");
  const [aiTone, setAiTone] = useState("friendly");
  const [previewMode, setPreviewMode] = useState({}); // { in_transit: "html" | "text" }

  const load = async () => {
    const r = await portalApi.getTemplates();
    setTemplates(r.data.templates); setVars(r.data.vars);
  };
  useEffect(() => { load(); }, []);

  // Debounced preview
  useEffect(() => {
    if (!templates) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      const results = {};
      for (const status of Object.keys(templates)) {
        try {
          const r = await portalApi.previewTemplate(templates[status].subject, templates[status].body);
          results[status] = r.data;
        } catch { results[status] = { subject: "(preview failed)", body: "" }; }
      }
      if (!cancelled) setPreviews(results);
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [templates]);

  const set = (status, field, val) => setTemplates((p) => ({ ...p, [status]: { ...p[status], [field]: val } }));

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await portalApi.saveTemplates(templates);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const rewrite = async (status) => {
    setAiBusy(status); setErr("");
    try {
      const r = await portalApi.aiRewriteTemplate(status, aiTone, templates[status].body);
      set(status, "body", r.data.body);
    } catch (e) { setErr(e?.response?.data?.detail || "AI rewrite failed"); }
    finally { setAiBusy(""); }
  };

  const canEdit = !customer?.organization_id || customer?.role === "owner";

  if (!templates) return <div className="text-sm text-[var(--tt-muted)]">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--tt-navy)]">Delivery emails</h1>
          <p className="text-[var(--tt-muted)] mt-1">The exact emails sent to your customers when their parcel moves through transit, out for delivery, and delivered.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-3">
            {savedAt && <span className="text-xs text-[var(--tt-muted)]">Saved at {savedAt}</span>}
            <button disabled={saving} onClick={save} data-testid="templates-save" className="btn-primary text-sm">{saving ? "Saving…" : "Save all templates"}</button>
          </div>
        )}
      </div>

      {!canEdit && <div className="card p-4 text-sm text-[var(--tt-muted)]">Only the account owner can edit templates. Ask your owner to make changes.</div>}
      {err && <div className="rounded-lg bg-red-50 text-red-700 text-sm p-3" data-testid="templates-error">{err}</div>}

      <div className="card p-4 text-xs">
        <div className="font-semibold uppercase tracking-widest text-[var(--tt-muted)] mb-2">Available variables</div>
        <div className="flex flex-wrap gap-2">
          {vars.map((v) => (
            <code key={v} className="rounded-lg bg-[var(--tt-mint-soft)] px-2 py-0.5 text-[var(--tt-navy)]" data-testid={`tpl-var-${v}`}>{`{{${v}}}`}</code>
          ))}
        </div>
      </div>

      {canEdit && (
        <div className="card p-4 flex items-center gap-3 text-sm">
          <span className="font-semibold text-[var(--tt-navy)]">AI rewrite tone:</span>
          <select value={aiTone} onChange={(e) => setAiTone(e.target.value)} data-testid="tpl-ai-tone" className="rounded-lg border border-[var(--tt-line)] bg-white px-3 py-1.5 text-sm">
            {TONE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <span className="text-xs text-[var(--tt-muted)]">Powered by Claude Sonnet 4.5</span>
        </div>
      )}

      <div className="space-y-6">
        {Object.entries(STATUS_LABELS).map(([status, meta]) => (
          <div key={status} className="card p-5" data-testid={`tpl-card-${status}`}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--tt-zam-orange)] font-semibold">{meta.label}</div>
                <div className="text-xs text-[var(--tt-muted)]">{meta.desc}</div>
              </div>
              {canEdit && (
                <button disabled={aiBusy === status} onClick={() => rewrite(status)} data-testid={`tpl-ai-${status}`} className="text-xs bg-gradient-to-r from-[var(--tt-green)] to-[var(--tt-zam-orange)] text-white rounded-lg px-3 py-1.5 flex items-center gap-1">
                  {aiBusy === status ? "Rewriting…" : <>✨ Rewrite with AI</>}
                </button>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-[var(--tt-muted)]">Subject</span>
                  <input value={templates[status].subject} onChange={(e) => set(status, "subject", e.target.value)} disabled={!canEdit} data-testid={`tpl-${status}-subject`} className="mt-1 w-full rounded-xl border border-[var(--tt-line)] bg-white px-3 py-2.5 text-sm" />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-[var(--tt-muted)]">Body</span>
                  <textarea value={templates[status].body} onChange={(e) => set(status, "body", e.target.value)} disabled={!canEdit} data-testid={`tpl-${status}-body`} rows={8} className="mt-1 w-full rounded-xl border border-[var(--tt-line)] bg-white px-3 py-2 text-sm font-mono leading-relaxed" />
                </label>
              </div>
              <div className="rounded-xl bg-[var(--tt-cream)] p-4 border border-dashed border-[var(--tt-line)]">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--tt-muted)] font-semibold">Live preview · branded HTML</div>
                  <div className="flex gap-1 text-[10px] uppercase tracking-widest">
                    {["html", "text"].map((mode) => (
                      <button key={mode} type="button" onClick={() => setPreviewMode({ ...previewMode, [status]: mode })} data-testid={`tpl-${status}-mode-${mode}`} className={`px-2 py-0.5 rounded ${(previewMode[status] || "html") === mode ? "bg-[var(--tt-green)] text-white" : "text-[var(--tt-muted)] hover:text-[var(--tt-navy)]"}`}>{mode}</button>
                    ))}
                  </div>
                </div>
                {(previewMode[status] || "html") === "html" && previews[status]?.html ? (
                  <iframe title={`preview-${status}`} srcDoc={previews[status].html} data-testid={`tpl-${status}-preview-html`} className="mt-3 w-full h-[420px] rounded-lg border border-[var(--tt-line)] bg-white" />
                ) : (
                  <div className="mt-3 space-y-2">
                    <div className="text-xs text-[var(--tt-muted)]">Subject:</div>
                    <div className="font-semibold text-[var(--tt-navy)]" data-testid={`tpl-${status}-preview-subject`}>{previews[status]?.subject || "…"}</div>
                    <div className="text-xs text-[var(--tt-muted)] mt-3">Body:</div>
                    <pre className="whitespace-pre-wrap font-sans text-sm text-[var(--tt-ink-soft)]" data-testid={`tpl-${status}-preview-body`}>{previews[status]?.body || "…"}</pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

